import AmmoFactory from 'ammojs-typed/wasm';
import ammoWasmUrl from 'ammojs-typed/ammo.wasm.wasm?url';
import { Vector3 } from 'three';
import { RigidBody } from './RigidBody.js';
import { createShape } from './shapes.js';
import { GROUP, MASK } from './groups.js';

/**
 * Owns the Ammo.js world. The game loop drives it at a fixed timestep:
 *
 *   world.fixedUpdate(dt)  -> game logic sets velocities / impulses
 *   physics.step(dt)       -> Bullet simulates
 *   physics.syncMeshes()   -> transforms are copied to Three.js meshes
 */
export class Physics {
  constructor({ gravity = [0, -9.81, 0] } = {}) {
    this.gravity = gravity;
    this.Ammo = null;
    this.world = null;
    this.records = new Set();
    this.byPointer = new Map();
    this._disposed = false;
  }

  async init() {
    // Ammo is a CommonJS emscripten bundle. Bundled ES modules run in strict
    // mode, so the factory needs an explicit `this`; without it the bundle
    // fails with "Cannot set properties of undefined (setting 'Ammo')".
    this.Ammo = await AmmoFactory.call({}, {
      locateFile: (file) => (file.endsWith('.wasm') ? ammoWasmUrl : file),
    });

    const Ammo = this.Ammo;

    this.collisionConfiguration = new Ammo.btDefaultCollisionConfiguration();
    this.dispatcher = new Ammo.btCollisionDispatcher(this.collisionConfiguration);
    this.broadphase = new Ammo.btDbvtBroadphase();
    this.solver = new Ammo.btSequentialImpulseConstraintSolver();
    this.world = new Ammo.btDiscreteDynamicsWorld(
      this.dispatcher,
      this.broadphase,
      this.solver,
      this.collisionConfiguration,
    );

    const gravity = new Ammo.btVector3(...this.gravity);
    this.world.setGravity(gravity);
    Ammo.destroy(gravity);

    return this;
  }

  get ready() {
    return Boolean(this.world);
  }

  get bodyCount() {
    return this.records.size;
  }

  /**
   * Add a rigid body to the world.
   *
   * @param {object} options
   * @param {object} options.shape       Shape description (see shapes.js).
   * @param {number} [options.mass=0]    0 = static.
   * @param {number[]} [options.position=[0,0,0]]
   * @param {number[]} [options.quaternion=[0,0,0,1]]
   * @param {THREE.Object3D|null} [options.mesh]
   * @param {number} [options.friction=0.6]
   * @param {number} [options.restitution=0]
   * @param {number} [options.linearDamping=0]
   * @param {number} [options.angularDamping=0.05]
   * @param {boolean} [options.lockRotation=false]
   * @param {boolean} [options.kinematic=false]
   * @param {boolean} [options.disableDeactivation=false]
   * @param {number} [options.group=GROUP.DEFAULT]
   * @param {number} [options.mask=MASK.ALL]
   * @param {object|null} [options.userData]
   * @returns {RigidBody}
   */
  addBody(options) {
    if (!this.world) throw new Error('Physics.addBody() called before Physics.init().');

    const {
      shape: shapeDescription,
      mass: requestedMass = 0,
      position = [0, 0, 0],
      quaternion = [0, 0, 0, 1],
      mesh = null,
      friction = 0.6,
      restitution = 0,
      linearDamping = 0,
      angularDamping = 0.05,
      lockRotation = false,
      kinematic = false,
      disableDeactivation = false,
      group = GROUP.DEFAULT,
      mask = MASK.ALL,
      userData = null,
      ccd = false,
    } = options ?? {};

    const Ammo = this.Ammo;
    const mass = kinematic ? 0 : requestedMass;

    if (shapeDescription.type === 'trimesh' && mass > 0) {
      throw new Error('Physics: trimesh shapes can only be used for static bodies. Give it mass 0.');
    }

    const { shape, auxiliaryShapes } = createShape(Ammo, shapeDescription);

    const transform = new Ammo.btTransform();
    transform.setIdentity();

    const origin = new Ammo.btVector3(position[0], position[1], position[2]);
    transform.setOrigin(origin);

    const rotation = new Ammo.btQuaternion(quaternion[0], quaternion[1], quaternion[2], quaternion[3]);
    transform.setRotation(rotation);

    const motionState = new Ammo.btDefaultMotionState(transform);

    Ammo.destroy(origin);
    Ammo.destroy(rotation);
    Ammo.destroy(transform);

    const localInertia = new Ammo.btVector3(0, 0, 0);
    if (mass > 0) shape.calculateLocalInertia(mass, localInertia);

    const constructionInfo = new Ammo.btRigidBodyConstructionInfo(mass, motionState, shape, localInertia);
    const native = new Ammo.btRigidBody(constructionInfo);

    Ammo.destroy(localInertia);
    Ammo.destroy(constructionInfo);

    native.setFriction(friction);
    native.setRestitution(restitution);
    native.setDamping(linearDamping, angularDamping);

    if (lockRotation) {
      const noRotation = new Ammo.btVector3(0, 0, 0);
      native.setAngularFactor(noRotation);
      Ammo.destroy(noRotation);
    }

    if (kinematic) {
      native.setCollisionFlags(native.getCollisionFlags() | 2); // CF_KINEMATIC_OBJECT
      native.setActivationState(4); // DISABLE_DEACTIVATION
    } else if (disableDeactivation) {
      native.setActivationState(4);
    }

    if (ccd) {
      native.setCcdMotionThreshold(0.1);
      native.setCcdSweptSphereRadius(0.2);
    }

    this.world.addRigidBody(native, group, mask);

    const record = new RigidBody(this, {
      native,
      motionState,
      shape,
      auxiliaryShapes,
      mesh,
      userData,
      group,
      mask,
      mass,
      kinematic,
    });

    this.records.add(record);
    this.byPointer.set(Ammo.getPointer(native), record);

    return record;
  }

  removeBody(record) {
    if (!record || record.disposed || !this.records.has(record)) return;

    const Ammo = this.Ammo;

    this.byPointer.delete(Ammo.getPointer(record.native));
    this.world.removeRigidBody(record.native);

    Ammo.destroy(record.native);
    Ammo.destroy(record.motionState);
    Ammo.destroy(record.shape);
    for (const auxiliary of record.auxiliaryShapes) Ammo.destroy(auxiliary);

    record._release();
    this.records.delete(record);
  }

  /**
   * Register a Bullet action (e.g. btRaycastVehicle) so it is updated inside
   * `stepSimulation`. Actions must be removed before their bodies are.
   */
  addAction(action) {
    if (!this.world || !action) return;
    this.world.addAction(action);
  }

  removeAction(action) {
    if (!this.world || !action) return;
    this.world.removeAction(action);
  }

  step(dt) {
    if (!this.world) return;
    // The game loop already fixed-steps, so ask Bullet for exactly one substep.
    this.world.stepSimulation(dt, 1, dt);
  }

  /** Copy every simulated body transform onto its associated Three.js mesh. */
  syncMeshes() {
    for (const record of this.records) record.sync();
  }

  /**
   * Closest-hit ray test.
   * @param {THREE.Vector3} from
   * @param {THREE.Vector3} to
   * @param {number} [mask=MASK.ALL] Bit mask of collision groups to include.
   * @returns {{ point: THREE.Vector3, normal: THREE.Vector3, body: RigidBody|null, distance: number } | null}
   */
  raycast(from, to, mask = MASK.ALL) {
    if (!this.world) return null;

    const Ammo = this.Ammo;
    const fromNative = new Ammo.btVector3(from.x, from.y, from.z);
    const toNative = new Ammo.btVector3(to.x, to.y, to.z);
    const callback = new Ammo.ClosestRayResultCallback(fromNative, toNative);
    callback.set_m_collisionFilterMask(mask);

    this.world.rayTest(fromNative, toNative, callback);

    let hit = null;
    if (callback.hasHit()) {
      const point = callback.get_m_hitPointWorld();
      const normal = callback.get_m_hitNormalWorld();
      const object = callback.get_m_collisionObject();

      hit = {
        point: new Vector3(point.x(), point.y(), point.z()),
        normal: new Vector3(normal.x(), normal.y(), normal.z()),
        body: this.byPointer.get(Ammo.getPointer(object)) ?? null,
        distance: 0,
      };
      hit.distance = from.distanceTo(hit.point);
    }

    Ammo.destroy(callback);
    Ammo.destroy(fromNative);
    Ammo.destroy(toNative);

    return hit;
  }

  /** Step physics a few times before the first frame so stacks settle. */
  settle(steps = 20, dt = 1 / 60) {
    for (let i = 0; i < steps; i += 1) this.step(dt);
    this.syncMeshes();
  }

  dispose() {
    if (this._disposed || !this.world) return;

    for (const record of [...this.records]) this.removeBody(record);

    const Ammo = this.Ammo;
    Ammo.destroy(this.world);
    Ammo.destroy(this.solver);
    Ammo.destroy(this.broadphase);
    Ammo.destroy(this.dispatcher);
    Ammo.destroy(this.collisionConfiguration);

    this.world = null;
    this.records.clear();
    this.byPointer.clear();
    this._disposed = true;
  }
}
